"use client";

import { useEffect, useState } from "react";
import { Row, Col, Form, Toast, ToastContainer, Image } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import NavList from "../NavList";
import { useUserStore } from "../../../store/user-store";
import { useDataFetch } from "../../../hooks/use-data-fetch";
import {
  City,
  Country,
  Province,
  useCities,
  useCountries,
  useProvinces,
} from "../../../hooks/use-locations";
import { API_BASE_URL } from "../../../config";

enum DocumentType {
  CC = "CC",
  CE = "CE",
  PASSPORT = "PASSPORT",
}


export interface ProfileFormData {
  name: string;
  email: string;
  cellphone: string;
  province: string;
  city: string;
  gender: string;
  dateOfBirth: string;
  country_id: string;
  document: string;
  documentType: DocumentType;
}

const AccountSettingsForm = () => {
  const { patchData: usePatch, invalidateQuery } = useDataFetch();
  const user = useUserStore((state) => state.user);
  const updateUserStore = useUserStore((state) => state.updateUser);

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastVariant, setToastVariant] = useState<"success" | "danger">(
    "success"
  );
  const [initialData, setInitialData] = useState<ProfileFormData | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormData>();

  const countryId = watch("country_id");
  const provinceId = watch("province");

  const { countries, isLoading: isLoadingCountries } = useCountries();
  const { provinces } = useProvinces(countryId);
  const { cities } = useCities(provinceId);

  const { mutateAsync: updateUser } = usePatch("/users/update/user", {});
  const { mutateAsync: updatePhoto } = usePatch(
    "/users/update/photo/profile/user",
    {}
  );

  useEffect(() => {
    if (user) {
      const formattedPhone =
        typeof user.cellphone === "number"
          ? `+${user.cellphone}`
          : typeof user.cellphone === "string" &&
            !user.cellphone.startsWith("+")
          ? `+${user.cellphone}`
          : user.cellphone || "";

      const formData: ProfileFormData = {
        name: user.name || "",
        email: user.email || "",
        cellphone: formattedPhone,
        province: user.province || "",
        city: user.city || "",
        gender: user.gender || "",
        dateOfBirth: user.dateOfBirth || "",
        country_id: user.country_id || "",
        document: user.document || "",
        documentType: user.documentType as DocumentType,  
      };

      reset(formData);
      setInitialData(formData);

      const photoUrl = `${API_BASE_URL}/users/show/photo/${user.photo || ""}`;
      setImagePreview(photoUrl);
    }
  }, [user, reset]);

  useEffect(() => {
    if (cities.length > 0 && user) {
      setValue("city", user.city);
    }
  }, [cities, setValue, user]);

  const onSubmit = async (data: ProfileFormData) => {
    if (JSON.stringify(data) === JSON.stringify(initialData)) {
      setToastMessage("No se detectaron cambios para guardar.");
      setToastVariant("danger");
      setShowToast(true);
      return;
    }

    try {
      await updateUser(data);
      invalidateQuery(["user-profile-init"]);
      setToastMessage("Perfil actualizado correctamente");
      setToastVariant("success");
      setShowToast(true);
      setInitialData(data);
      updateUserStore({ ...user, ...data });
    } catch (e: any) {
      setToastMessage(e.message || "Error al actualizar perfil");
      setToastVariant("danger");
      setShowToast(true);
    }
  };

  const handleImageChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const previewUrl = URL.createObjectURL(file);
    setImagePreview(previewUrl);

    const reader = new FileReader();
    reader.onloadend = async () => {
      const base64 = reader.result as string;

      try {
        const response = await updatePhoto({
          file: {
            name: file.name,
            base64URL: base64,
          },
        });

        if (response.message === "photo_uploaded_successfully") {
          setToastMessage("Imagen de perfil actualizada");
          setToastVariant("success");
          setShowToast(true);
        }
      } catch (error: any) {
        setToastMessage("Error al subir la imagen");
        setToastVariant("danger");
        setShowToast(true);
      }
    };

    reader.readAsDataURL(file);
  };

  return (
    <div className="bg-white p-4 rounded-3 mb-4 position-relative">
      <NavList />
      {/* <label>Foto de perfil</label> */}

      {isLoadingCountries ? (
        <p>Cargando países...</p>
      ) : (
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col md={12} className="mb-3 d-flex flex-column border rounded-3 p-4 justify-content-center align-items-center" style={{ width: "200px", marginLeft: "12px" }}>
              <label className="position-relative" style={{ cursor: "pointer" }}>
                {imagePreview ? (
                  <Image
                    src={imagePreview}
                    roundedCircle
                    width={120}
                    height={120}
                    style={{ objectFit: "cover", marginBottom: "10px" }}
                  />
                ): <p>Seleccionar foto</p>}
                <Form.Control
                  placeholder="Seleccionar foto"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={handleImageChange}
                />
              </label>
            </Col>
          </Row>
          <Row>
            <Col lg={6} className="mb-3">
            <Form.Group>
              <label>Tipo de documento</label>
              <Form.Select
                style={{ height: "50px" }}
                {...register("documentType", {
                  required: "Selecciona el tipo de documento",
                })}
                isInvalid={!!errors.documentType}
              >
                <option value="">Selecciona...</option>
                <option value={DocumentType.CC}>Cédula de ciudadanía</option>
                <option value={DocumentType.CE}>Cédula de extranjería</option>
                <option value={DocumentType.PASSPORT}>Pasaporte</option>
              </Form.Select>
              <Form.Control.Feedback type="invalid">
                {errors.documentType?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

            <Col lg={6} className="mb-3">
              <Form.Group>
                <label>Documento</label>
                <Form.Control
                  type="text"
                  {...register("document", {
                      required: "El documento es obligatorio",
                      minLength: {
                        value: 6,
                        message: "El documento debe tener al menos 6 dígitos",
                      },
                      maxLength: {
                        value: 12,
                        message: "El documento no puede exceder 12 dígitos",
                      },
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "El documento solo puede contener números",
                      },
                    })}
                  isInvalid={!!errors.document}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.document?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col lg={6} className="mb-3">
              <Form.Group>
                <label>Nombre</label>
                <Form.Control
                  type="text"
                  readOnly
                  style={{
                    cursor: "not-allowed",
                  }}
                  {...register("name", {
                    required: "El nombre es obligatorio",
                  })}
                  isInvalid={!!errors.name}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.name?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col lg={6} className="mb-3 ">
              <Form.Group>
                <label>Correo</label>
                <Form.Control
                  type="email"
                  readOnly
                  style={{
                    cursor: "not-allowed",
                  }}
                  {...register("email", {
                    required: "El correo es obligatorio",
                    pattern: {
                      value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
                      message: "Correo inválido",
                    },
                  })}
                  isInvalid={!!errors.email}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.email?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col lg={6} className="mb-3">
              <Form.Group>
                <label>Teléfono</label>
                <Controller
                  name="cellphone"
                  control={control}
                  rules={{ required: "El teléfono es obligatorio" }}
                  render={({ field, fieldState }) => (
                    <>
                      <PhoneInput
                        {...field}
                        defaultCountry="CO"
                        value={field.value || ""}
                        onChange={field.onChange}
                        className={`form-control phone-input-dark ${
                          fieldState.invalid ? "is-invalid" : ""
                        }`}
                      />

                      {fieldState.error && (
                        <div className="invalid-feedback d-block">
                          {fieldState.error.message}
                        </div>
                      )}
                    </>
                  )}
                />
              </Form.Group>
            </Col>

            <Col lg={6} className="mb-3">
              <Form.Group>
                <label>País</label>
                <Form.Select
                  style={{ height: "50px" }}
                  {...register("country_id", {
                    required: "Selecciona un país",
                  })}
                  isInvalid={!!errors.country_id}
                >
                  <option value="">Selecciona un país</option>
                  {countries.map((country: Country) => (
                    <option key={country.id} value={country.id}>
                      {country.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.country_id?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col lg={6} className="mb-3">
              <Form.Group>
                <label>Provincia</label>
                <Form.Select
                  style={{ height: "50px" }}
                  {...register("province", { required: "Campo obligatorio" })}
                  isInvalid={!!errors.province}
                >
                  <option value="">Selecciona una provincia</option>
                  {provinces.map((province: Province) => (
                    <option key={province.id} value={province.id}>
                      {province.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.province?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col lg={6} className="mb-3">
              <Form.Group>
                <label>Ciudad</label>
                <Form.Select
                  style={{ height: "50px" }}
                  {...register("city", { required: "Campo obligatorio" })}
                  isInvalid={!!errors.city}
                >
                  <option value="">Selecciona una ciudad</option>
                  {cities.map((city: City) => (
                    <option key={city.id} value={city.id}>
                      {city.name}
                    </option>
                  ))}
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.city?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col lg={6} className="mb-3">
              <Form.Group>
                <label>Género</label>
                <Form.Select
                  style={{ height: "50px" }}
                  {...register("gender", { required: "Selecciona un género" })}
                  isInvalid={!!errors.gender}
                >
                  <option value="">Selecciona...</option>
                  <option value="masculino">Masculino</option>
                  <option value="femenino">Femenino</option>
                  <option value="otro">Otro</option>
                </Form.Select>
                <Form.Control.Feedback type="invalid">
                  {errors.gender?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>

            <Col lg={6} className="mb-3">
              <Form.Group>
                <label>Fecha de nacimiento</label>
                <Form.Control
                  type="date"
                  {...register("dateOfBirth", {
                    required: "Campo obligatorio",
                  })}
                  isInvalid={!!errors.dateOfBirth}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.dateOfBirth?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Col>
          </Row>

          <button
            type="submit"
            disabled={isSubmitting}
            className="btn btn-primary mt-2"
          >
            {isSubmitting ? "Guardando..." : "Guardar Cambios"}
          </button>
        </Form>
      )}

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          bg={toastVariant}
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </div>
  );
};

export default AccountSettingsForm;
